import { useRef, useState } from "react"
import { Undo2Icon } from "lucide-react"
import { Chat, ChatMessage, ChatPart } from "@/components/chat/chat"
import { Button } from "@/components/ui/button"
import { loadModel, type HarnessMessage } from "@/lib/harness"
import { design, fakeDesigner } from "@/lib/harness/designer"
import { ModelSelect } from "./modelselect"

const MODEL_KEY = "playbooks.designer.model"

interface Props {
  playbook: Playbook
  team?: string
  realAI: boolean
  onChange: (playbook: Playbook) => void
}

function stored() {
  try { return localStorage.getItem(MODEL_KEY) ?? "" } catch { return "" }
}

function toHistory(messages: ChatMessage[]): HarnessMessage[] {
  return messages.map(m => ({ role: m.role === "user" ? "user" as const : "assistant" as const, text: m.text ?? (m.parts ?? []).flatMap(p => p.type === "text" ? [p.text] : []).join("\n\n") })).filter(m => m.text)
}

export function Designer({ playbook, team, realAI, onChange }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [busy, setBusy] = useState(false)
  const [undo, setUndo] = useState<Playbook[]>([])
  const [model, setModel] = useState(stored)
  const counter = useRef(0)

  function handleModel(value: string) {
    setModel(value)
    try { localStorage.setItem(MODEL_KEY, value) } catch { }
  }

  async function handleSend(text: string) {
    const id = `a${counter.current++}`
    const update = (parts: ChatPart[]) => setMessages(prev => prev.map(m => m.id === id ? { ...m, parts } : m))
    const history = toHistory(messages)
    const before = playbook
    let changed = false
    setMessages(prev => [...prev, { id: `u${counter.current++}`, role: "user", text }, { id, role: "agent", parts: [] }])
    setBusy(true)
    try {
      if (realAI && !model) throw new Error("Pick a model for the designer")
      const ai = realAI ? await loadModel(team!, model) : fakeDesigner
      await design({ model: ai, playbook, history, message: text, onChange: next => { changed = true; onChange(next) }, onParts: update })
    } catch (e) {
      update([{ type: "error", text: (e as Error).message }])
    }
    if (changed) setUndo(prev => [...prev, before])
    setBusy(false)
  }

  function handleUndo() {
    onChange(undo[undo.length - 1])
    setUndo(undo.slice(0, -1))
  }

  return <Chat messages={messages} busy={busy} onSend={handleSend} onReset={() => setMessages([])} title="Designer" agentLabel="Designer" placeholder="Ask for a change"
    actions={<>
      {realAI && <div className="w-48"><ModelSelect value={model} onChange={handleModel} placeholder="Designer model" /></div>}
      <Button variant="outline" size="icon" aria-label="Undo" title="Undo the last turn of the designer" onClick={handleUndo} disabled={busy || !undo.length}><Undo2Icon /></Button>
    </>}
  />
}
